import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import {
  Package,
  Eye,
  Loader2,
  Image,
  CheckCircle,
  XCircle,
  Clock,
  Upload,
  Printer,
  Download,
  LayoutGrid,
  List,
  Trash2,
  Search,
  Layers,
} from 'lucide-react';
import { api, type Listing } from '../api/client';

type ViewMode = 'grid' | 'list';
type ProductTypeFilter = 'all' | 'digital_download' | 'printify_pod';

export default function Inventory() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [productTypeFilter, setProductTypeFilter] = useState<ProductTypeFilter>('all');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');

  const { data, isLoading } = useQuery({
    queryKey: ['listings'],
    queryFn: () => api.listings.list(),
  });

  const deleteListing = useMutation({
    mutationFn: (id: number) => api.listings.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['listings'] });
    },
  });

  const listings = data?.listings ?? [];

  // Apply filters
  const filteredListings = listings.filter((l: Listing) => {
    const statusMatch = statusFilter === 'all' || l.status === statusFilter;
    const typeMatch =
      productTypeFilter === 'all' || (l.product_type || 'digital_download') === productTypeFilter;
    return statusMatch && typeMatch;
  });

  // Count by product type
  const digitalCount = listings.filter(
    (l: Listing) => (l.product_type || 'digital_download') === 'digital_download'
  ).length;
  const podCount = listings.filter((l: Listing) => l.product_type === 'printify_pod').length;

  const getStatusBadge = (status: string) => {
    const styles: Record<string, { bg: string; icon: typeof Clock | null; animate?: boolean }> = {
      draft: { bg: 'bg-gray-100 text-gray-700', icon: Clock },
      generating: { bg: 'bg-blue-100 text-blue-700', icon: Loader2, animate: true },
      ready: { bg: 'bg-green-100 text-green-700', icon: CheckCircle },
      syncing: { bg: 'bg-orange-100 text-orange-700', icon: Upload, animate: true },
      published: { bg: 'bg-purple-100 text-purple-700', icon: CheckCircle },
      sync_failed: { bg: 'bg-red-100 text-red-700', icon: XCircle },
    };
    return styles[status] || styles.draft;
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      draft: 'Draft',
      generating: 'Generating',
      ready: 'Ready',
      syncing: 'Syncing',
      published: 'Published',
      sync_failed: 'Sync Failed',
    };
    return labels[status] || status;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Package className="w-8 h-8 text-gray-600" />
          <h1 className="text-3xl font-bold text-gray-900">Inventory</h1>
        </div>
        <div className="flex items-center gap-4">
          {/* Product Type Filter */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setProductTypeFilter('all')}
              className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                productTypeFilter === 'all'
                  ? 'bg-gray-900 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              All ({listings.length})
            </button>
            <button
              onClick={() => setProductTypeFilter('digital_download')}
              className={`px-3 py-1.5 text-sm rounded-lg transition-colors flex items-center gap-1.5 ${
                productTypeFilter === 'digital_download'
                  ? 'bg-blue-600 text-white'
                  : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
              }`}
            >
              <Download className="w-3.5 h-3.5" />
              Digital ({digitalCount})
            </button>
            <button
              onClick={() => setProductTypeFilter('printify_pod')}
              className={`px-3 py-1.5 text-sm rounded-lg transition-colors flex items-center gap-1.5 ${
                productTypeFilter === 'printify_pod'
                  ? 'bg-purple-600 text-white'
                  : 'bg-purple-50 text-purple-700 hover:bg-purple-100'
              }`}
            >
              <Printer className="w-3.5 h-3.5" />
              POD ({podCount})
            </button>
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input w-auto text-sm py-1.5"
          >
            <option value="all">All Status</option>
            <option value="draft">Draft</option>
            <option value="generating">Generating</option>
            <option value="ready">Ready</option>
            <option value="syncing">Syncing</option>
            <option value="published">Published</option>
            <option value="sync_failed">Sync Failed</option>
          </select>

          {/* View Mode Toggle */}
          <div className="flex items-center border rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-1.5 ${viewMode === 'grid' ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
              title="Grid view"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-1.5 ${viewMode === 'list' ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
              title="List view"
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      ) : filteredListings.length === 0 ? (
        <div className="card bg-gradient-to-br from-gray-50 to-slate-50 border-gray-200 text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Package className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No listings yet</h3>
          <p className="text-gray-500 mb-6 max-w-sm mx-auto">
            {statusFilter !== 'all' || productTypeFilter !== 'all'
              ? 'No listings match your current filters. Try adjusting them.'
              : 'Start by researching trending niches, then generate designs.'}
          </p>
          {statusFilter === 'all' && productTypeFilter === 'all' && (
            <div className="flex items-center justify-center gap-3">
              <Link to="/research" className="btn btn-primary flex items-center gap-2">
                <Search className="w-4 h-4" />
                Research Niches
              </Link>
              <Link to="/batch" className="btn btn-secondary flex items-center gap-2">
                <Layers className="w-4 h-4" />
                Batch Generate
              </Link>
            </div>
          )}
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {filteredListings.map((listing: Listing) => (
            <Link
              key={listing.id}
              to={`/listing/${listing.id}`}
              className="card p-3 hover:shadow-md transition-shadow group"
            >
              {/* Compact Image */}
              <div className="aspect-[4/3] bg-gray-100 rounded-lg mb-2 flex items-center justify-center overflow-hidden relative">
                {listing.primary_image ? (
                  <img
                    src={listing.primary_image}
                    alt={listing.title || 'Product'}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Image className="w-8 h-8 text-gray-300" />
                )}
                {/* Product Type Badge */}
                <span
                  className={`absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                    listing.product_type === 'printify_pod'
                      ? 'bg-purple-500 text-white'
                      : 'bg-blue-500 text-white'
                  }`}
                >
                  {listing.product_type === 'printify_pod' ? 'POD' : 'Digital'}
                </span>
                {/* Delete Button */}
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (confirm(`Delete listing "${listing.title || 'Untitled'}"?`)) {
                      deleteListing.mutate(listing.id);
                    }
                  }}
                  className="absolute top-1.5 right-1.5 p-1 rounded bg-white/80 hover:bg-red-100 text-gray-500 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Delete listing"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Content */}
              <div className="space-y-1">
                <h3 className="text-sm font-medium line-clamp-2 group-hover:text-orange-600 transition-colors">
                  {listing.title || 'Untitled Listing'}
                </h3>
                <div className="flex items-center justify-between">
                  {(() => {
                    const statusInfo = getStatusBadge(listing.status);
                    const StatusIcon = statusInfo.icon;
                    return (
                      <span
                        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${statusInfo.bg}`}
                      >
                        {StatusIcon && (
                          <StatusIcon
                            className={`w-2.5 h-2.5 ${statusInfo.animate ? 'animate-spin' : ''}`}
                          />
                        )}
                        {getStatusLabel(listing.status)}
                      </span>
                    );
                  })()}
                  {listing.base_price && (
                    <span className="text-xs font-medium text-gray-600">
                      ${(listing.base_price / 100).toFixed(2)}
                    </span>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        /* List View */
        <div className="space-y-2">
          {filteredListings.map((listing: Listing) => (
            <Link
              key={listing.id}
              to={`/listing/${listing.id}`}
              className="card p-3 flex items-center gap-4 hover:shadow-md transition-shadow group"
            >
              {/* Thumbnail */}
              <div className="w-16 h-16 bg-gray-100 rounded-lg flex-shrink-0 flex items-center justify-center overflow-hidden">
                {listing.primary_image ? (
                  <img
                    src={listing.primary_image}
                    alt={listing.title || 'Product'}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Image className="w-6 h-6 text-gray-300" />
                )}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-medium truncate group-hover:text-orange-600 transition-colors">
                  {listing.title || 'Untitled Listing'}
                </h3>
                <div className="flex items-center gap-2 mt-1">
                  <span
                    className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                      listing.product_type === 'printify_pod'
                        ? 'bg-purple-100 text-purple-700'
                        : 'bg-blue-100 text-blue-700'
                    }`}
                  >
                    {listing.product_type === 'printify_pod' ? 'POD' : 'Digital'}
                  </span>
                  {listing.niche_keyword && (
                    <span className="text-xs text-gray-500">{listing.niche_keyword}</span>
                  )}
                </div>
              </div>

              {/* Status & Price */}
              <div className="flex items-center gap-4 flex-shrink-0">
                {listing.base_price && (
                  <span className="text-sm font-medium text-gray-700">
                    ${(listing.base_price / 100).toFixed(2)}
                  </span>
                )}
                {(() => {
                  const statusInfo = getStatusBadge(listing.status);
                  const StatusIcon = statusInfo.icon;
                  return (
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusInfo.bg}`}
                    >
                      {StatusIcon && (
                        <StatusIcon
                          className={`w-3 h-3 ${statusInfo.animate ? 'animate-spin' : ''}`}
                        />
                      )}
                      {getStatusLabel(listing.status)}
                    </span>
                  );
                })()}
                <Eye className="w-4 h-4 text-gray-400 group-hover:text-orange-500" />
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (confirm(`Delete listing "${listing.title || 'Untitled'}"?`)) {
                      deleteListing.mutate(listing.id);
                    }
                  }}
                  className="p-1.5 rounded hover:bg-red-100 text-gray-400 hover:text-red-600 transition-colors"
                  title="Delete listing"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
